import { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "../../context/ThemeContext";
import {
  doc,
  getDoc,
  collection,
  query,
  limit,
  getDocs,
} from "firebase/firestore";
import { db } from "../../firebase.config";

const SupportModal = ({ showSupport, setShowSupport }) => {
  const { theme } = useTheme();
  const [copied, setCopied] = useState(false);
  const [supportEmail, setSupportEmail] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSupportEmail = async () => {
      try {
        const q = query(collection(db, "admin_credential"), limit(1));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const firstDoc = querySnapshot.docs[0];
          const data = firstDoc.data();
          if (data.support_email) {
            setSupportEmail(data.support_email);
          }
        }
      } catch (error) {
        console.error("Error fetching support email:", error);
      } finally {
        setLoading(false);
      }
    };

    if (showSupport) {
      fetchSupportEmail();
    }
  }, [showSupport]);

  const handleCloseModal = () => {
    setShowSupport(false);
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(supportEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEmailClick = () => {
    const subject = "Support Request - Ascendra";
    const body = "Hi Support,\n\nI need help with...";
    const mailtoLink = `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    let didHide = false;

    const onBlur = () => {
      didHide = true; // likely app opened
    };

    window.addEventListener("blur", onBlur);

    window.location.href = mailtoLink;

    setTimeout(() => {
      window.removeEventListener("blur", onBlur);

      if (!didHide) {
        window.open(
          `https://mail.google.com/mail/?view=cm&to=${supportEmail}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
          "_blank",
        );
      }
    }, 1500);
  };
  if (!showSupport) return null;

  const isDark = theme === "dark";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div
        className={`relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden ${
          isDark
            ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-slate-700"
            : "bg-white border border-gray-200"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`relative px-6 py-5 border-b ${
            isDark ? "border-slate-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center justify-center gap-3">
            <div
              className={`p-2 rounded-full ${
                isDark ? "bg-emerald-500/20" : "bg-emerald-100"
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-6 w-6 ${
                  isDark ? "text-emerald-400" : "text-emerald-600"
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            </div>
            <h2
              className={`text-xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Need Support?
            </h2>
          </div>
          <button
            onClick={handleCloseModal}
            className={`absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full transition-colors ${
              isDark
                ? "text-slate-400 hover:text-white hover:bg-slate-700"
                : "text-gray-400 hover:text-gray-900 hover:bg-gray-100"
            }`}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          <p
            className={`text-center mb-6 ${
              isDark ? "text-slate-300" : "text-gray-600"
            }`}
          >
            We're here to help! Reach out to our support team and we'll get back
            to you as soon as possible.
          </p>

          {/* Email Card */}
          <div
            className={`rounded-xl p-4 mb-6 ${
              isDark
                ? "bg-slate-800/50 border border-slate-700"
                : "bg-gray-50 border border-gray-200"
            }`}
          >
            <p
              className={`text-xs uppercase tracking-wide mb-2 ${
                isDark ? "text-slate-400" : "text-gray-500"
              }`}
            >
              Email Us At
            </p>
            <div className="flex items-center justify-between">
              {loading ? (
                <div
                  className={`h-6 w-40 rounded animate-pulse ${
                    isDark ? "bg-slate-700" : "bg-gray-200"
                  }`}
                />
              ) : (
                <span
                  className={`text-lg font-semibold ${
                    isDark ? "text-emerald-400" : "text-emerald-600"
                  }`}
                >
                  {supportEmail}
                </span>
              )}
              <button
                onClick={handleCopyEmail}
                disabled={loading}
                className={`p-2 rounded-lg transition-all ${
                  copied
                    ? isDark
                      ? "bg-emerald-500/20 text-emerald-400"
                      : "bg-emerald-100 text-emerald-600"
                    : isDark
                      ? "hover:bg-slate-700 text-slate-400"
                      : "hover:bg-gray-200 text-gray-500"
                } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                title="Copy email"
              >
                {copied ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Response Time */}
          <div
            className={`flex items-center justify-center gap-2 mb-6 text-sm ${
              isDark ? "text-slate-400" : "text-gray-500"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>Typical response time: Within 24 hours</span>
          </div>

          {/* Send Email Button */}
          <button
            onClick={handleEmailClick}
            disabled={loading}
            className={`w-full py-3 px-6 rounded-xl font-semibold text-white transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
            style={{
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              boxShadow: "0 8px 24px rgba(16, 185, 129, 0.4)",
            }}
          >
            <span className="flex items-center justify-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Send Email
            </span>
          </button>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 text-center text-xs border-t ${
            isDark
              ? "border-slate-700 text-slate-500"
              : "border-gray-200 text-gray-400"
          }`}
        >
          Your journey matters to us 💚
        </div>
      </div>
    </div>
  );
};

export default SupportModal;
